import { h as __h } from "preact";
import { Component, h } from "preact";
import { enterpriseOrgResponse } from "~/backend/validate.js";
/** @import { sessionEnterpriseResponse } from "~/backend/validate.js"; */
import { paginatedResponse, query } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import Avatar from "~/component/Avatar.js";
import Data from "~/component/Data.js";
import Dropdown from "~/component/Dropdown.js";
import Icon from "~/component/Icon.js";
/** @import { TriggerProps } from "~/component/Tooltip.js"; */
import Tooltip from "~/component/Tooltip.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import adminStyle from "~/admin/lib/style.js";
import { createOrg } from "~/admin/res/org/actions/createOrg.js";
import { deleteOrg } from "~/admin/res/org/actions/deleteOrg.js";
import button from "~/style/button.js";
import util from "~/style/util.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Organizations.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Organizations.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {sessionEnterpriseResponse} enterprise
 */
export default class Organizations extends Component {
  #orgs = query(() => ({
    key: [`/v2/enterprises/${this.props.enterprise.id}/orgs`, {
      limit: 9999
    }]
  }), {
    schema: paginatedResponse(enterpriseOrgResponse)
  });
  render() {
    const enterpriseId = this.props.enterprise.id;
    return [__h(Crumb, {}, "Organizations"), __h("div", {
      "class": adminStyle.main
    }, h(Data, {
      loading: this.#orgs.loading && !this.#orgs.data,
      error: this.#orgs.error,
      children: __h("ul", {
        "class": css.orgGrid
      }, this.#orgs.data?.data.map(org => __h("li", {
        key: org.id
      }, __h(OrgCard, {
        org: org,
        enterpriseId: enterpriseId,
        isGroupSyncEnabled: this.props.enterprise.is_group_sync_enabled
      }))), __h("li", {}, __h("button", {
        type: "button",
        "class": css.newOrgCard,
        onClick: () => createOrg({
          enterpriseId
        })
      }, __h(Icon, {
        name: "plus",
        size: 16
      }), __h("span", {}, "New organization"))))
    }))];
  }
}

/**
 * @typedef {object} OrgCardProps
 * @property {enterpriseOrgResponse} org
 * @property {string} enterpriseId
 * @property {boolean} isGroupSyncEnabled
 * @param {OrgCardProps} props
 */
function OrgCard(props) {
  const {
    org,
    enterpriseId,
    isGroupSyncEnabled
  } = props;
  const isAccessible = identity.canAccessOrg(org.id);
  const menuId = `org-menu-${org.id}`;
  return __h("div", {
    "class": util.card + " " + css.orgCard,
    "data-accessible": isAccessible
  }, isAccessible ? __h("a", {
    "class": css.orgOverlay,
    href: `/org/${org.id}/`
  }, __h("span", {
    "class": util.visuallyHidden
  }, "Open ", org.name)) : __h(Tooltip, {
    placement: "top",
    contents: isGroupSyncEnabled ? `Map ${org.name} to a directory group you're in, as admin, to access it.` : `Give yourself an admin role in ${org.name} on the members page to access it.`
  }, (/** @type {TriggerProps} */trigger) => __h("button", {
    type: "button",
    "class": css.orgOverlay,
    ...trigger,
    "aria-disabled": "true"
  }, __h("span", {
    "class": util.visuallyHidden
  }, org.name))), __h("div", {
    "class": css.orgInfo
  }, __h(Avatar, {
    name: org.name,
    size: 32
  }), __h("span", {
    "class": css.orgName
  }, org.name)), __h(Dropdown, {
    id: menuId,
    placement: "bottom-end",
    trigger: __h("button", {
      type: "button",
      "class": button.button + " " + css.orgMenu,
      "data-level": "tertiary",
      "data-size": "small",
      popovertarget: menuId
    }, __h("span", {
      "class": util.visuallyHidden
    }, "Actions for ", org.name), __h(Icon, {
      name: "menu",
      size: 14
    })),
    items: [{
      icon: "trash",
      text: "Delete organization",
      kind: "negative",
      onSelect: () => deleteOrg({
        enterpriseId
      }, org)
    }]
  }));
}